import { isMobile, isTablet } from "./utilities-BFAI3Dmv.js";
import { setListCap, getViewportStateKey, getNaturalListHeight, fitDropdownToViewport } from "./dropdown-height-cap-CWIO39S_.js";
import "./waapi-patch-BCt4pEht.js";
import "./cookie-banner-layout-DZP7Q0uT.js";
import "./section-anchor-scroll-Bvm-mXVl.js";
import "./mobile-anim-gate-Btn1wVuD.js";
class TeleportElement extends HTMLElement {
  constructor() {
    super();
    this.openT = null;
    this.closeT = null;
    this.HOVER_OPEN_DELAY = 80;
    this.HOVER_CLOSE_DELAY = 250;
    this._isPortaled = false;
    this.handleEnter = this.handleEnter.bind(this);
    this.handleLeave = this.handleLeave.bind(this);
    this.handleScroll = this.handleScroll.bind(this);
    this.handleOutsideClick = this.handleOutsideClick.bind(this);
    this.handleBreakpointChange = this.handleBreakpointChange.bind(this);
  }
  connectedCallback() {
    this.placement = this.getAttribute("placement") || "tooltip";
    this.triggerType = this.getAttribute("trigger") || "hover";
    const triggerSelector = this.getAttribute("trigger-selector");
    if (triggerSelector) {
      const parent = this.parentElement || document;
      this.trigger = parent.querySelector(triggerSelector);
    } else {
      this.trigger = this.closest("[data-tooltip-trigger]") || this.previousElementSibling;
    }
    if (!this.trigger) return;
    if (this.trigger.hasAttribute("data-tooltip-trigger")) {
      this.triggerType = this.trigger.getAttribute("data-tooltip-trigger");
    }
    this.tooltip = this.querySelector("[data-teleport-content]") || this.firstElementChild;
    if (!this.tooltip) return;
    this.globalLayer = document.querySelector(".global-teleport-inner");
    if (!this.globalLayer) return;
    this.handleResize = () => {
      if (this._isPortaled) this.updatePosition();
    };
    this._mobileMediaQuery = window.matchMedia("(max-width: 991.98px)");
    this._mobileMediaQuery.addEventListener("change", this.handleBreakpointChange);
    this.setupListeners();
  }
  disconnectedCallback() {
    var _a;
    this.removeListeners();
    (_a = this._mobileMediaQuery) == null ? void 0 : _a.removeEventListener("change", this.handleBreakpointChange);
    this._mobileMediaQuery = null;
    this._cancelPendingWork();
    this._restoreTooltip();
  }
  setupListeners() {
    if (this.triggerType === "hover" && this.trigger) {
      this.trigger.addEventListener("mouseenter", this.handleEnter);
      this.trigger.addEventListener("mouseleave", this.handleLeave);
      this.trigger.addEventListener("focusin", this.handleEnter);
      this.trigger.addEventListener("focusout", this.handleLeave);
      this.tooltip.addEventListener("mouseenter", this.handleEnter);
      this.tooltip.addEventListener("mouseleave", this.handleLeave);
      this.tooltip.addEventListener("focusin", this.handleEnter);
      this.tooltip.addEventListener("focusout", this.handleLeave);
    }
  }
  removeListeners() {
    if (this.triggerType === "hover" && this.trigger) {
      this.trigger.removeEventListener("mouseenter", this.handleEnter);
      this.trigger.removeEventListener("mouseleave", this.handleLeave);
      this.trigger.removeEventListener("focusin", this.handleEnter);
      this.trigger.removeEventListener("focusout", this.handleLeave);
      if (this.tooltip) {
        this.tooltip.removeEventListener("mouseenter", this.handleEnter);
        this.tooltip.removeEventListener("mouseleave", this.handleLeave);
        this.tooltip.removeEventListener("focusin", this.handleEnter);
        this.tooltip.removeEventListener("focusout", this.handleLeave);
      }
    }
    window.removeEventListener("scroll", this.handleScroll, { passive: true });
    window.removeEventListener("resize", this.handleResize, { passive: true });
    document.removeEventListener("click", this.handleOutsideClick);
  }
  cancelClose() {
    if (this.closeT) {
      clearTimeout(this.closeT);
      this.closeT = null;
    }
  }
  cancelOpen() {
    if (this.openT) {
      clearTimeout(this.openT);
      this.openT = null;
    }
  }
  _cancelPendingWork() {
    this.cancelOpen();
    this.cancelClose();
    clearTimeout(this._restoreTimer);
    this._restoreTimer = null;
    if (this._openFrame) cancelAnimationFrame(this._openFrame);
    if (this._activateFrame) cancelAnimationFrame(this._activateFrame);
    if (this._updateFrame) cancelAnimationFrame(this._updateFrame);
    this._openFrame = null;
    this._activateFrame = null;
    this._updateFrame = null;
  }
  handleBreakpointChange() {
    this._cancelPendingWork();
    this.close({ immediate: true });
  }
  handleEnter() {
    if (isMobile() || isTablet() || !window.matchMedia("(hover: hover)").matches) return;
    this.cancelClose();
    if (this._isPortaled || this.tooltip.classList.contains("active")) return;
    this.openT = setTimeout(() => {
      this.open();
      this.cancelClose();
    }, this.HOVER_OPEN_DELAY);
  }
  handleLeave() {
    if (isMobile() || isTablet() || !window.matchMedia("(hover: hover)").matches) return;
    this.cancelOpen();
    this.closeT = setTimeout(() => {
      this.close();
    }, this.HOVER_CLOSE_DELAY);
  }
  handleOutsideClick(e) {
    if (this._isPortaled) {
      if (!this.trigger.contains(e.target) && !this.tooltip.contains(e.target)) {
        this.close();
      }
    }
  }
  open() {
    if (this._isPortaled && this.tooltip.classList.contains("active")) return;
    if (!this._isPortaled) {
      this._isPortaled = true;
      const triggerRect = this.trigger.getBoundingClientRect();
      this.tooltip.classList.add("is-portaled", "teleport-setup");
      this.globalLayer.appendChild(this.tooltip);
      this.updatePosition(triggerRect);
    } else {
      this.updatePosition();
    }
    window.addEventListener("scroll", this.handleScroll, { passive: true });
    window.addEventListener("resize", this.handleResize, { passive: true });
    if (this.triggerType !== "manual") {
      document.addEventListener("click", this.handleOutsideClick);
    }
    clearTimeout(this._restoreTimer);
    this._restoreTimer = null;
    this._openFrame = requestAnimationFrame(() => {
      this._openFrame = null;
      if (!this._isPortaled) return;
      this.tooltip.classList.remove("teleport-setup");
      this._activateFrame = requestAnimationFrame(() => {
        this._activateFrame = null;
        if (this._isPortaled) this.tooltip.classList.add("active");
      });
    });
  }
  close({ immediate = false } = {}) {
    this.cancelOpen();
    this.cancelClose();
    if (this._openFrame) cancelAnimationFrame(this._openFrame);
    if (this._activateFrame) cancelAnimationFrame(this._activateFrame);
    this._openFrame = null;
    this._activateFrame = null;
    if (!this._isPortaled) {
      if (immediate) this._restoreTooltip();
      return;
    }
    this.tooltip.classList.remove("active");
    window.removeEventListener("scroll", this.handleScroll, { passive: true });
    window.removeEventListener("resize", this.handleResize, { passive: true });
    if (this.triggerType !== "manual") {
      document.removeEventListener("click", this.handleOutsideClick);
    }
    clearTimeout(this._restoreTimer);
    if (immediate) {
      this._restoreTooltip();
      return;
    }
    this._restoreTimer = setTimeout(() => {
      this._restoreTimer = null;
      if (!this._isPortaled || this.tooltip.classList.contains("active")) return;
      this._restoreTooltip();
    }, 300);
  }
  _restoreTooltip() {
    if (!this.tooltip) return;
    if (this.tooltip.parentNode !== this) this.appendChild(this.tooltip);
    this._isPortaled = false;
    this.tooltip.classList.remove(
      "active",
      "is-portaled",
      "teleport-setup",
      "tooltip-bottom",
      "dropdown-bottom",
      "dropdown-top"
    );
    this.tooltip.style.left = "";
    this.tooltip.style.top = "";
    this.tooltip.style.bottom = "";
  }
  handleScroll() {
    if (this._isPortaled) {
      this.updatePosition();
    }
  }
  updatePosition(cachedTriggerRect = null) {
    if (!this.trigger || !this.tooltip) return;
    const triggerRect = cachedTriggerRect || this.trigger.getBoundingClientRect();
    const tooltipRect = this.tooltip.getBoundingClientRect();
    const scrollX = window.scrollX || window.pageXOffset;
    const scrollY = window.scrollY || window.pageYOffset;
    const innerWidth = window.innerWidth;
    const innerHeight = window.innerHeight;
    const computed = getComputedStyle(this.tooltip);
    let rawGap = computed.getPropertyValue("--small-gap").trim() || "0";
    let gap = parseFloat(rawGap) * (rawGap.includes("rem") ? 16 : 1) || 12;
    let rawDropdownGap = computed.getPropertyValue("--teleport-dropdown-gap").trim();
    let dropdownGap = rawDropdownGap !== "" ? parseFloat(rawDropdownGap) * (rawDropdownGap.includes("rem") ? 16 : 1) || 0 : gap;
    let x, y;
    let isBottom = false;
    let dropDirection = "bottom";
    let listCap;
    if (this.placement === "dropdown") {
      x = triggerRect.left + scrollX;
      if (x + tooltipRect.width > innerWidth - 10) {
        x = triggerRect.right + scrollX - tooltipRect.width;
      }
      if (x < 10) x = 10;
      const fit = this._fitDropdown(triggerRect, tooltipRect, gap);
      let height = tooltipRect.height;
      if (fit) {
        dropDirection = fit.side;
        height = fit.height;
        listCap = fit.listMax;
      } else if (triggerRect.bottom + tooltipRect.height + dropdownGap > innerHeight && triggerRect.top - tooltipRect.height - dropdownGap > 0) {
        dropDirection = "top";
      }
      y = dropDirection === "top" ? triggerRect.top + scrollY - height - dropdownGap : triggerRect.bottom + scrollY + dropdownGap;
    } else {
      x = triggerRect.left + scrollX + triggerRect.width / 2 - tooltipRect.width / 2;
      y = triggerRect.top + scrollY - tooltipRect.height - gap;
      if (triggerRect.top - tooltipRect.height - gap < 0) {
        y = triggerRect.bottom + scrollY + gap;
        isBottom = true;
      }
      if (x < 10) x = 10;
      if (x + tooltipRect.width > innerWidth - 10) x = innerWidth - tooltipRect.width - 10;
    }
    if (this.placement === "tooltip") {
      if (isBottom) this.tooltip.classList.add("tooltip-bottom");
      else this.tooltip.classList.remove("tooltip-bottom");
    } else {
      if (dropDirection === "top") {
        this.tooltip.classList.add("dropdown-top");
        this.tooltip.classList.remove("dropdown-bottom");
      } else {
        this.tooltip.classList.add("dropdown-bottom");
        this.tooltip.classList.remove("dropdown-top");
      }
    }
    if (this._updateFrame) cancelAnimationFrame(this._updateFrame);
    this._updateFrame = requestAnimationFrame(() => {
      this._updateFrame = null;
      if (!this._isPortaled) return;
      this.tooltip.style.left = `${x}px`;
      this.tooltip.style.top = `${y}px`;
      if (listCap !== void 0) setListCap(this._scrollList, listCap);
    });
  }
  /**
   * Dropdowns with a scroll list open below, above, or on the roomier side
   * with the list capped (see dropdown-height-cap). The fit is computed once
   * per trigger position and viewport size; scroll and resize change both.
   */
  _fitDropdown(triggerRect, tooltipRect, gap) {
    this._scrollList ?? (this._scrollList = this.tooltip.querySelector("scroll-shadow"));
    const list = this._scrollList;
    if (!list) return null;
    const key = getViewportStateKey(triggerRect.top, triggerRect.bottom);
    if (key === this._fitKey) return this._fit;
    const naturalList = getNaturalListHeight(list);
    const chrome = tooltipRect.height - list.offsetHeight;
    const { side, listMax } = fitDropdownToViewport({
      anchorTop: triggerRect.top,
      anchorBottom: triggerRect.bottom,
      naturalHeight: chrome + naturalList,
      chrome,
      gap
    });
    this._fitKey = key;
    this._fit = { side, listMax, height: chrome + (listMax ?? naturalList) };
    return this._fit;
  }
}
if (!customElements.get("teleport-element")) {
  customElements.define("teleport-element", TeleportElement);
}
